// @ts-ignore
import { Network } from '@fuul/sdk-solana';
import { clusterApiUrl } from '@solana/web3.js';

// Solana chain IDs (matching backend fuul-server/src/shared/networks.ts)
export const SOLANA_CHAIN_IDS = {
  MAINNET: 101,
  DEVNET: 201,
  FOGO: 102,
} as const;

export type SolanaChainId = (typeof SOLANA_CHAIN_IDS)[keyof typeof SOLANA_CHAIN_IDS];

export interface SolanaNetworkConfig {
  chainId: SolanaChainId;
  network: Network; // SDK network enum
  rpcUrls: string[]; // Multiple for fallback
  name: string;
  isTestnet: boolean;
  explorerUrl: string;
  nativeCurrencySymbol: string;
  explorerName: string;
}

export const SOLANA_NETWORKS: Record<SolanaChainId, SolanaNetworkConfig> = {
  [SOLANA_CHAIN_IDS.MAINNET]: {
    chainId: SOLANA_CHAIN_IDS.MAINNET,
    network: Network.MAINNET,
    rpcUrls: [
      process.env.NEXT_PUBLIC_SOLANA_RPC_MAINNET,
      process.env.NEXT_PUBLIC_SOLANA_RPC_MAINNET_FALLBACK,
      clusterApiUrl('mainnet-beta'),
    ].filter(Boolean) as string[],
    name: 'Solana',
    isTestnet: false,
    explorerUrl: 'https://solscan.io',
    nativeCurrencySymbol: 'SOL',
    explorerName: 'Solscan',
  },
  [SOLANA_CHAIN_IDS.DEVNET]: {
    chainId: SOLANA_CHAIN_IDS.DEVNET,
    network: Network.DEVNET,
    rpcUrls: [
      process.env.NEXT_PUBLIC_SOLANA_RPC_DEVNET,
      process.env.NEXT_PUBLIC_SOLANA_RPC_DEVNET_FALLBACK,
      clusterApiUrl('devnet'),
    ].filter(Boolean) as string[],
    name: 'Solana Devnet',
    isTestnet: true,
    explorerUrl: 'https://solscan.io',
    nativeCurrencySymbol: 'SOL',
    explorerName: 'Solscan',
  },
  [SOLANA_CHAIN_IDS.FOGO]: {
    chainId: SOLANA_CHAIN_IDS.FOGO,
    network: Network.FOGO_MAINNET,
    rpcUrls: [process.env.NEXT_PUBLIC_FOGO_RPC_MAINNET].filter(Boolean) as string[],
    name: 'Fogo',
    isTestnet: false,
    explorerUrl: 'https://explorer.fogo.io',
    nativeCurrencySymbol: 'FOGO',
    explorerName: 'Explorer',
  },
};

// All Solana chain IDs for easy checking
const SOLANA_CHAIN_ID_SET = new Set(Object.values(SOLANA_CHAIN_IDS));

export function isSolanaChainId(chainId: string | number | null | undefined): chainId is SolanaChainId {
  if (!chainId) return false;
  const numericId = Number(chainId);
  return SOLANA_CHAIN_ID_SET.has(numericId as SolanaChainId);
}

export function getSolanaNetworkConfig(chainId: number): SolanaNetworkConfig {
  if (!isSolanaChainId(chainId)) {
    throw new Error(`Invalid Solana chainId: ${chainId}`);
  }
  return SOLANA_NETWORKS[chainId];
}

export function getSolanaExplorerTxUrl(signature: string, chainId: SolanaChainId | string | number): string {
  // Ensure chainId is numeric for comparison
  const numericChainId = Number(chainId) as SolanaChainId;
  const config = SOLANA_NETWORKS[numericChainId];

  if (!config) {
    // Fallback to mainnet Solscan if config not found
    return `https://solscan.io/tx/${signature}`;
  }

  // Solscan cluster param: devnet or omit for mainnet
  let cluster = '';
  if (numericChainId === SOLANA_CHAIN_IDS.DEVNET) {
    cluster = '?cluster=devnet';
  }
  // Note: Fogo uses its own explorer (explorer.fogo.io) which doesn't need cluster param

  return `${config.explorerUrl}/tx/${signature}${cluster}`;
}
